import { DragEvent, useRef, useState } from "react";
import { CheckCircle2, Loader2, UploadCloud, X } from "lucide-react";
import { useEmployeeOptions } from "#/features/employees/hooks";
import { useUploadDocument } from "../hooks";
import type { DocumentModuleCode, DocumentVisibility } from "../types";

// ── Constants ─────────────────────────────────────────────────────────────────

const MODULE_OPTS: { value: DocumentModuleCode; label: string }[] = [
  { value: "LEAVE",       label: "Leave" },
  { value: "CLAIM",       label: "Claim" },
  { value: "TRAINING",    label: "Training" },
  { value: "REQUISITION", label: "Requisition" },
  { value: "INVENTORY",   label: "Inventory" },
];

const ACCEPT        = ".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx";
const ACCEPT_TYPES  = [
  "application/pdf",
  "image/png", "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = {
  /**
   * When provided, the form is operating in "contextual" mode (embedded inside
   * a leave/training/claim detail page). moduleCode and referenceId are locked.
   * When omitted, the admin mode renders dropdowns for the user to choose them.
   */
  moduleCode?: DocumentModuleCode | string;
  referenceId?: number;
  onUploaded?: (doc?: { id: number; originalFileName: string }) => void;
};

// ── Component ─────────────────────────────────────────────────────────────────

export function DocumentUploadForm({ moduleCode: propModule, referenceId: propRef, onUploaded }: Props) {
  const isContextual = !!propModule && !!propRef;

  const [docType,    setDocType]    = useState("");
  const [module,     setModule]     = useState<DocumentModuleCode | "">(
    (propModule as DocumentModuleCode) ?? "",
  );
  const [refId,      setRefId]      = useState(propRef ? String(propRef) : "");
  const [employeeId, setEmployeeId] = useState("");
  const [dragOver,   setDragOver]   = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const upload  = useUploadDocument();
  // Only offered in admin (non-contextual) mode — e.g. uploading a payslip or contract
  // for someone. Contextual uploads (from a leave/claim/training record) default to self.
  const { data: employeeOptions } = useEmployeeOptions();

  const resolvedModule = isContextual ? (propModule as DocumentModuleCode) : module;
  const resolvedRefId  = isContextual ? propRef! : parseInt(refId, 10);

  async function handleFile(file: File) {
    setError(null);
    setSuccessMsg(null);

    if (!ACCEPT_TYPES.includes(file.type)) {
      setError("Accepted formats: PDF, PNG, JPG, DOC, DOCX, XLS, XLSX.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File must be under 5 MB.");
      return;
    }
    if (!resolvedModule) {
      setError("Please select a module.");
      return;
    }
    if (!resolvedRefId || isNaN(resolvedRefId)) {
      setError("Please enter a valid record ID.");
      return;
    }

    try {
      const doc = await upload.mutateAsync({
        file,
        meta: {
          moduleCode:   resolvedModule,
          referenceId:  resolvedRefId,
          documentType: docType || undefined,
          employeeId:   !isContextual && employeeId ? Number(employeeId) : undefined,
        },
      });
      setSuccessMsg(`"${doc.originalFileName}" uploaded successfully.`);
      setDocType("");
      if (!isContextual) { setRefId(""); setEmployeeId(""); }
      onUploaded?.(doc);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? "Upload failed.");
    }
  }

  function onInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = "";
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  return (
    <div className="space-y-3">
      {/* Admin mode — module + record ID selectors */}
      {!isContextual && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-400">
              Module <span className="text-red-500">*</span>
            </label>
            <select
              className="input w-full"
              value={module}
              onChange={(e) => setModule(e.target.value as DocumentModuleCode)}
            >
              <option value="">— Select module —</option>
              {MODULE_OPTS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-400">
              Record ID <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              className="input w-full"
              placeholder="e.g. 42"
              value={refId}
              onChange={(e) => setRefId(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-400">
              On behalf of <span className="text-slate-400">(optional — defaults to you)</span>
            </label>
            <select
              className="input w-full"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">— Myself —</option>
              {employeeOptions?.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.fullName}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Document type label (optional free-text) */}
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase text-slate-400">
          Document type <span className="text-slate-400">(optional)</span>
        </label>
        <input
          type="text"
          className="input w-full"
          placeholder='e.g. "Invoice", "Certificate", "ID Copy"'
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
        />
      </div>

      {/* Drop zone */}
      {upload.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4">
          <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
          <span className="text-sm text-slate-600">Uploading…</span>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragOver
              ? "border-emerald-400 bg-emerald-50"
              : "border-slate-300 bg-white hover:border-emerald-400 hover:bg-emerald-50/30"
          }`}
        >
          <UploadCloud className={`mx-auto mb-2 h-8 w-8 ${dragOver ? "text-emerald-500" : "text-slate-400"}`} />
          <p className="text-sm font-semibold text-slate-700">
            {dragOver ? "Drop file here" : "Click or drag to upload"}
          </p>
          <p className="mt-1 text-xs text-slate-400">PDF, PNG, JPG, DOC, DOCX, XLS, XLSX — max 5 MB</p>
          <input ref={fileRef} type="file" className="hidden" accept={ACCEPT} onChange={onInput} />
        </div>
      )}

      {successMsg && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {successMsg}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <X className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}
